import Core from "./core";
import { useContext, useEffect, useState } from "react";
import restService, {
  getKeycloakAccessToken,
  keycloak_agent,
} from "../../restService";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import UserContext from "../../contexts/UserContext";
import CampaignContext from "../../contexts/CampaignContext";
import CampaignDetailsContext from "../../contexts/CampaignDetailsContext";
import { PulseLoader } from "react-spinners";
import { KEYCLOAK_TOKEN } from "../../utilities";
import { getMembers } from "../../lib";
import TransactionsPage from "./Transactions";
const Home = ({}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location.pathname;
  const queryParams = location.search;

  const [searchParams] = useSearchParams();
  const txId_ = searchParams.get("txId");
  const appId_ = searchParams.get("id");

  const [loading, setLoading] = useState(true);

  const [campaign, setCampaign] = useContext(CampaignContext);
  const [user, setUser] = useContext(UserContext);
  const [CampaignDetails, setCampaignDetails] = useContext(
    CampaignDetailsContext
  );

  useEffect(() => {
    authenticate();
    handleAUTH();
  }, []);

  function handleRedirect(path, role) {
    if (role && role === "customer-support") {
      navigate("/transactions", { replace: true });
      return;
    }

    if (pathname === "/" || pathname === path) {
      navigate(path, { replace: true });
      return;
    }

    const currentUrl = pathname + queryParams;
    navigate(currentUrl);
  }

  function logout() {
    sessionStorage.clear();
    restService.Auth.redirectToSignIn();
  }

  const authenticate = async () => {
    setLoading(true);
    const userDetails = sessionStorage.getItem("USER");
    const campaignDetails = sessionStorage.getItem("CAMPAIGN");

    if (txId_ && appId_) {
      const _resolveUser = resolveUser(txId_, appId_);
      const _keycloak = getKeycloakToken();

      await Promise.allSettled([_resolveUser, _keycloak]);
      setLoading(false);

      return;
    }

    if (userDetails && campaignDetails) {
      const __userSession = JSON.parse(userDetails);
      const __campaignSession = JSON.parse(campaignDetails);

      const sessionTxId = __userSession?.txId;
      const sessionAppId = __campaignSession?.id;

      if (sessionTxId && sessionAppId) {
        const _resolveUser = resolveUser(
          __userSession?.txId,
          __campaignSession?.id
        );
        const _keycloak = getKeycloakToken();

        await Promise.allSettled([_resolveUser, _keycloak]);
        return;
      }

      logout();
      return;
    }

    logout();
  };

  const resolveUser = async (txtId, appId) => {
    try {
      const {
        data: { results, message },
      } = await restService.Auth.validateUser(txtId, appId);

      if (message?.toLowerCase() == "success") {
        await resolveCompanyCampaign(appId, results);
        return;
      }
      logout();
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const resolveCompanyCampaign = async (appId, validUser) => {
    try {
      const {
        data: { results },
      } = await restService.Auth.getCompany(appId);

      const campaignDetails = await getCampaignDetails(appId);
      const campaignDetailsIsObject = typeof campaignDetails === "object";

      setCampaign(results);

      sessionStorage.setItem("CAMPAIGN", JSON.stringify(results));

      const role = await getCurrentUserRole({ id: appId }, validUser);

      const __user = { ...validUser, role: role || "agent" };

      setUser(__user);
      sessionStorage.setItem("USER", JSON.stringify(__user));
      sessionStorage.setItem("ROLE", role); // save this in memory

      if (
        campaignDetailsIsObject &&
        Object.keys(campaignDetails)?.length > 0 &&
        campaignDetails?.campaignType &&
        campaignDetails?.campaignType?.toLowerCase()?.includes("survey")
      ) {
        handleRedirect("/survey-response", role);
        return;
      }

      if (role?.toLowerCase() === "customer-support") {
        handleRedirect("/transactions", role);

        return;
      }

      handleRedirect("/dashboard", role);
    } catch (e) {
      console.log(e);
    }
  };

  const getCampaignDetails = async (appId) => {
    try {
      const {
        data: { data },
      } = await restService.Auth.getCampaignDetails(appId);


      setCampaignDetails(data);
      sessionStorage.setItem("CAMPAIGN_DETAILS", JSON.stringify(data));

      return data;
    } catch (error) {
      console.log(error);
      return {};
    }
  };

  const handleAUTH = () => {
    const AUTHuser = sessionStorage.getItem("USER");

    if (
      (location.pathname.includes("dashboard") ||
        location.pathname.includes("entries")) &&
      !AUTHuser
    ) {
      logout();
    }
  };

  /**
   * Retrieves the Keycloak token from the session storage and sets it as the authorization header for the keycloak_agent.
   * If the token is not found in the session storage, it retrieves a new token using the getKeycloakAccessToken function.
   * @returns {Promise<void>} A promise that resolves once the token is retrieved and set.
   */
  async function getKeycloakToken() {
    const keycloakToken = sessionStorage.getItem(KEYCLOAK_TOKEN);

    if (keycloakToken) {
      keycloak_agent.defaults.headers.common["Authorization"] =
        "Bearer " + keycloakToken;

      return;
    }

    try {
      const data = await getKeycloakAccessToken(
        "campaign-frontend",
        "password"
      );

      sessionStorage.setItem(KEYCLOAK_TOKEN, data.access_token);

      keycloak_agent.defaults.headers.common["Authorization"] =
        "Bearer " + data.access_token;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Retrieves the role of the current user in a campaign.
   * @param {string} campaign - The campaign identifier.
   * @param {object} user - The user object.
   * @returns {string} The role of the current user in the campaign.
   */
  async function getCurrentUserRole(campaign, user) {
    try {
      const campaignMembers = await getMembers(campaign, user);

      const role = campaignMembers?.find(
        (member) => member?.email === user?.email
      )?.role;

      return role;
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  return (
    <div>
      {loading && (
        <div className="h-screen w-full flex items-center justify-center">
          <PulseLoader color="#FF6A3A" />
        </div>
      )}

      {!loading && (
        <>
          {user && user?.role?.toLowerCase() === "customer-support" ? (
            <Routes>
              <Route path="/transactions" element={<TransactionsPage />} />
            </Routes>
          ) : (
            <Core />
          )}
        </>
      )}
    </div>
  );
};

export default Home;
